import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronRight, Image as ImageIcon, ArrowLeft, ExternalLink, Upload, RefreshCw, History } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useContentCtx } from "@/contexts/ContentContext";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/data/siteContent";

/* Deep merge identical to ContentContext (kept local to avoid cycle).
   Type-strict: base shape wins on type mismatch — guarantees draft.X.Y.Z is always reachable. */
function safeMerge(base, override) {
    if (override === undefined || override === null) return base;
    const baseIsArr = Array.isArray(base);
    const overIsArr = Array.isArray(override);
    if (baseIsArr && overIsArr) return override;
    if (baseIsArr !== overIsArr) return base;
    const baseIsObj = base && typeof base === "object";
    const overIsObj = override && typeof override === "object";
    if (baseIsObj && overIsObj) {
        const out = { ...base };
        for (const k of Object.keys(override)) {
            out[k] = k in base ? safeMerge(base[k], override[k]) : override[k];
        }
        return out;
    }
    if (baseIsObj !== overIsObj) return base;
    return override;
}

const LOGO = "https://customer-assets.emergentagent.com/job_ff078f22-bc2b-467a-b845-6483b385dad8/artifacts/c2lq75mw_image.png";

/* ============== Reusable atoms ============== */
function Field({ label, hint, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="label-k">{label}</label>
            {children}
            {hint && <p className="text-[11px] text-kayama-brown-3 mt-1">{hint}</p>}
        </div>
    );
}
function TextInput({ value, onChange, placeholder, type = "text", testid }) {
    return (
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="input-k"
            data-testid={testid}
        />
    );
}
function TextArea({ value, onChange, rows = 4, placeholder, testid }) {
    return (
        <textarea
            rows={rows}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="input-k resize-none"
            data-testid={testid}
        />
    );
}
function ImageInput({ value, onChange, testid }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    async function onPick(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image trop volumineuse (max 8 MB).");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Photo uploadée !");
        } catch (err) {
            toast.error(formatApiError(err, "Upload impossible."));
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex items-stretch gap-2">
                <div className="flex items-center gap-3 flex-1">
                    <ImageIcon size={16} className="text-kayama-gold shrink-0" />
                    <input
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="https://exemple.com/photo.jpg ou /api/media/…"
                        className="input-k"
                        data-testid={testid}
                    />
                </div>
                <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid={testid ? `${testid}-file` : undefined} />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2 border border-kayama-pink text-kayama-pink hover:bg-kayama-pink hover:text-white text-xs uppercase tracking-[0.15em] transition-colors disabled:opacity-50"
                    data-testid={testid ? `${testid}-upload` : undefined}
                >
                    <Upload size={14} /> {uploading ? "Envoi…" : "Charger"}
                </button>
            </div>
            {value && (
                <div className="border border-kayama-line w-40 h-28 overflow-hidden bg-kayama-muted">
                    <img src={resolveMediaUrl(value)} alt="Aperçu" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.opacity = 0.2)} />
                </div>
            )}
        </div>
    );
}
function Section({ title, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-kayama-paper border border-kayama-line">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between p-5 hover:bg-kayama-cream/50 transition"
            >
                <span className="font-heading text-xl text-kayama-brown">{title}</span>
                {open ? <ChevronDown size={18} className="text-kayama-pink" /> : <ChevronRight size={18} className="text-kayama-brown-3" />}
            </button>
            {open && <div className="p-6 border-t border-kayama-line">{children}</div>}
        </div>
    );
}
function SubBtn({ children, onClick, danger, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-[0.18em] transition-colors ${
                danger
                    ? "border-kayama-line text-kayama-pink hover:bg-kayama-pink hover:text-white"
                    : "border-kayama-gold text-kayama-brown hover:bg-kayama-gold hover:text-white"
            }`}
            {...rest}
        >
            {children}
        </button>
    );
}

/* ============== Helper: nested updater ============== */
function setPath(obj, path, value) {
    const next = Array.isArray(obj) ? [...obj] : { ...obj };
    let cur = next;
    for (let i = 0; i < path.length - 1; i++) {
        const k = path[i];
        cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
        cur = cur[k];
    }
    cur[path[path.length - 1]] = value;
    return next;
}

/* ============== Main component ============== */
export default function AdminContent() {
    const { user, logout } = useAuth();
    const { content, refresh } = useContentCtx();
    const [draft, setDraft] = useState(() => safeMerge(DEFAULT_CONTENT, content));
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [tab, setTab] = useState("brand");
    const nav = useNavigate();
    const dirtyRef = useRef(false);

    // Keep draft synced with latest content ONLY when there are no unsaved local edits.
    // This prevents focus/visibility re-fetches from wiping the admin's in-progress changes.
    useEffect(() => {
        if (!dirtyRef.current) {
            setDraft(safeMerge(DEFAULT_CONTENT, content));
        }
    }, [content]);

    const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(safeMerge(DEFAULT_CONTENT, content)), [draft, content]);

    useEffect(() => {
        dirtyRef.current = dirty;
    }, [dirty]);

    function update(path, value) {
        setDraft((d) => setPath(d, path, value));
    }

    async function save() {
        setSaving(true);
        try {
            await api.put("/admin/content", draft);
            dirtyRef.current = false;
            await refresh();
            toast.success("Contenu enregistré ! Le site public reflète vos modifications. Si un onglet du site est déjà ouvert, il se mettra à jour automatiquement quand vous y reviendrez (sinon rechargez la page).", { duration: 6000 });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function reloadFromServer() {
        if (dirty && !window.confirm("Vous avez des modifications non enregistrées. Recharger depuis le serveur va les écraser. Continuer ?")) {
            return;
        }
        setRefreshing(true);
        try {
            dirtyRef.current = false;
            const fresh = await refresh();
            // After refresh() the context updates; force draft sync now
            setDraft(safeMerge(DEFAULT_CONTENT, fresh ?? content));
            toast.success("Données actualisées depuis le serveur.");
        } catch (err) {
            toast.error(formatApiError(err, "Impossible d'actualiser."));
        } finally {
            setRefreshing(false);
        }
    }

    const [historyOpen, setHistoryOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    async function openHistory() {
        setHistoryOpen(true);
        setLoadingHistory(true);
        try {
            const { data } = await api.get("/admin/content/history");
            setHistory(data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoadingHistory(false);
        }
    }

    async function restoreSnapshot(snapshot_at) {
        if (!window.confirm("Restaurer cette version ? Le contenu actuel sera automatiquement sauvegardé avant la restauration.")) return;
        try {
            await api.post(`/admin/content/restore/${encodeURIComponent(snapshot_at)}`);
            const fresh = await refresh();
            setDraft(safeMerge(DEFAULT_CONTENT, fresh ?? content));
            dirtyRef.current = false;
            setHistoryOpen(false);
            toast.success("Contenu restauré depuis l'historique.");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    function resetToDefaults(section) {
        if (!window.confirm(`Réinitialiser la section « ${section} » aux valeurs par défaut ?`)) return;
        setDraft((d) => ({ ...d, [section]: structuredClone(DEFAULT_CONTENT[section]) }));
        toast.message("Section réinitialisée. N'oubliez pas d'enregistrer.");
    }

    function handleLogout() {
        logout();
        nav("/admin/login");
    }

    return (
        <div className="min-h-screen bg-kayama-cream" data-testid="page-admin-content">
            <header className="bg-kayama-paper border-b border-kayama-line sticky top-0 z-30">
                <div className="container-k flex items-center justify-between py-4 flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="text-kayama-brown-3 hover:text-kayama-pink" data-testid="content-back">
                            <ArrowLeft size={18} />
                        </Link>
                        <img src={LOGO} alt="Kayama Admin" className="h-10 w-auto" />
                        <span className="font-heading text-2xl text-kayama-brown hidden md:inline">Édition du site</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={openHistory}
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-kayama-brown hover:text-kayama-pink"
                            data-testid="content-history-btn"
                            title="Voir et restaurer une version précédente"
                        >
                            <History size={14} /> Historique
                        </button>
                        <button
                            onClick={reloadFromServer}
                            disabled={refreshing}
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-kayama-brown hover:text-kayama-pink disabled:opacity-50"
                            data-testid="content-refresh"
                            title="Recharger le contenu actuel du serveur"
                        >
                            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                            {refreshing ? "Actualisation…" : "Actualiser"}
                        </button>
                        <Link to="/" target="_blank" className="link-gold text-[11px] flex items-center gap-1" data-testid="content-preview">
                            Voir le site <ExternalLink size={12} />
                        </Link>
                        <span className="text-xs text-kayama-brown-2 hidden md:inline">{user?.email}</span>
                        <button onClick={handleLogout} className="btn-outline-k" data-testid="content-logout">
                            <LogOut size={14} /> Déconnexion
                        </button>
                    </div>
                </div>
                <div className="bg-kayama-muted border-t border-kayama-line">
                    <div className="container-k py-3 flex items-center justify-between gap-4 flex-wrap">
                        <p className="text-xs text-kayama-brown-2">
                            {dirty ? <span className="text-kayama-pink font-semibold">● Modifications non enregistrées</span> : <span>Toutes les modifications sont enregistrées</span>}
                        </p>
                        <div className="flex items-center gap-2">
                            <SubBtn onClick={() => setDraft(safeMerge(DEFAULT_CONTENT, content))} danger>Annuler</SubBtn>
                            <button onClick={save} disabled={!dirty || saving} className="btn-primary-k disabled:opacity-50" data-testid="content-save">
                                <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="container-k py-10">
                <p className="overline">Gestion de contenu</p>
                <h1 className="mt-2 font-heading text-4xl md:text-5xl font-light text-kayama-brown">
                    Modifiez tous les textes et photos de votre site
                </h1>
                <p className="mt-3 text-kayama-brown-2 max-w-2xl text-sm">
                    Toutes les sections de votre site sont éditables ci-dessous. Collez simplement l'URL d'une image (Instagram, Imgur, votre hébergement, etc.) pour la remplacer.
                </p>

                <Tabs value={tab} onValueChange={setTab} className="mt-8">
                    <TabsList className="bg-kayama-paper rounded-none p-1 border border-kayama-line flex flex-wrap h-auto w-full justify-start">
                        {[
                            ["brand", "Marque"],
                            ["contact", "Coordonnées"],
                            ["accueil", "Accueil"],
                            ["apropos", "À propos"],
                            ["services", "Services"],
                            ["galerie", "Galerie"],
                            ["menus", "Menus"],
                            ["daily_menus", "Menus du jour"],
                            ["contact_page", "Page Contact"],
                        ].map(([v, l]) => (
                            <TabsTrigger
                                key={v}
                                value={v}
                                data-testid={`tab-${v}`}
                                className="rounded-none data-[state=active]:bg-kayama-pink data-[state=active]:text-white text-xs uppercase tracking-[0.15em]"
                            >
                                {l}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {/* BRAND */}
                    <TabsContent value="brand" className="space-y-6 mt-8">
                        <SectionHeader title="Identité de marque" onReset={() => resetToDefaults("brand")} />
                        <div className="bg-kayama-paper border border-kayama-line p-6 grid md:grid-cols-2 gap-6">
                            <Field label="Phrase rose (script)">
                                <TextInput value={draft.brand?.script_pink} onChange={(v) => update(["brand", "script_pink"], v)} />
                            </Field>
                            <Field label="Phrase dorée (script)">
                                <TextInput value={draft.brand?.script_gold} onChange={(v) => update(["brand", "script_gold"], v)} />
                            </Field>
                            <Field label="Slogan du footer" full>
                                <TextInput value={draft.brand?.footer_tagline} onChange={(v) => update(["brand", "footer_tagline"], v)} />
                            </Field>
                        </div>
                    </TabsContent>

                    {/* CONTACT */}
                    <TabsContent value="contact" className="space-y-6 mt-8">
                        <SectionHeader title="Coordonnées du traiteur" onReset={() => resetToDefaults("contact")} />
                        <div className="bg-kayama-paper border border-kayama-line p-6 grid md:grid-cols-2 gap-6">
                            <Field label="Téléphone (affiché)"><TextInput value={draft.contact?.phone} onChange={(v) => update(["contact", "phone"], v)} /></Field>
                            <Field label="Téléphone (format tel: ex +33...)"><TextInput value={draft.contact?.phone_href} onChange={(v) => update(["contact", "phone_href"], v)} /></Field>
                            <Field label="Email"><TextInput value={draft.contact?.email} onChange={(v) => update(["contact", "email"], v)} /></Field>
                            <Field label="Pseudo Instagram"><TextInput value={draft.contact?.instagram_handle} onChange={(v) => update(["contact", "instagram_handle"], v)} /></Field>
                            <Field label="URL Instagram"><TextInput value={draft.contact?.instagram_url} onChange={(v) => update(["contact", "instagram_url"], v)} /></Field>
                            <Field label="Site web (affiché)"><TextInput value={draft.contact?.website_label} onChange={(v) => update(["contact", "website_label"], v)} placeholder="www.kayamasaveurs.com" /></Field>
                            <Field label="Site web (URL complète)"><TextInput value={draft.contact?.website_url} onChange={(v) => update(["contact", "website_url"], v)} placeholder="https://www.kayamasaveurs.com" /></Field>
                            <Field label="Zones d'intervention"><TextInput value={draft.contact?.zone} onChange={(v) => update(["contact", "zone"], v)} /></Field>
                        </div>
                    </TabsContent>

                    {/* ACCUEIL */}
                    <TabsContent value="accueil" className="space-y-4 mt-8">
                        <SectionHeader title="Page d'accueil" onReset={() => resetToDefaults("accueil")} />
                        <Section title="Hero (en haut de la page)" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline (petit texte du haut)" full><TextInput value={draft.accueil?.hero.overline} onChange={(v) => update(["accueil", "hero", "overline"], v)} /></Field>
                                <Field label="Titre - 1ère ligne"><TextInput value={draft.accueil?.hero.h1_part1} onChange={(v) => update(["accueil", "hero", "h1_part1"], v)} /></Field>
                                <Field label="Titre - en italique rose"><TextInput value={draft.accueil?.hero.h1_italic} onChange={(v) => update(["accueil", "hero", "h1_italic"], v)} /></Field>
                                <Field label="Titre - 3e ligne"><TextInput value={draft.accueil?.hero.h1_part3} onChange={(v) => update(["accueil", "hero", "h1_part3"], v)} /></Field>
                                <Field label="Phrase script doré"><TextInput value={draft.accueil?.hero.script} onChange={(v) => update(["accueil", "hero", "script"], v)} /></Field>
                                <Field label="Sous-titre / description" full><TextArea rows={3} value={draft.accueil?.hero.subtitle} onChange={(v) => update(["accueil", "hero", "subtitle"], v)} /></Field>
                                <Field label="Bouton principal"><TextInput value={draft.accueil?.hero.cta_primary} onChange={(v) => update(["accueil", "hero", "cta_primary"], v)} /></Field>
                                <Field label="Bouton secondaire"><TextInput value={draft.accueil?.hero.cta_secondary} onChange={(v) => update(["accueil", "hero", "cta_secondary"], v)} /></Field>
                                <Field label="Image hero (URL)" full><ImageInput value={draft.accueil?.hero.image} onChange={(v) => update(["accueil", "hero", "image"], v)} /></Field>
                                <Field label="Badge - titre"><TextInput value={draft.accueil?.hero.badge_title} onChange={(v) => update(["accueil", "hero", "badge_title"], v)} /></Field>
                                <Field label="Badge - sous-titre"><TextInput value={draft.accueil?.hero.badge_subtitle} onChange={(v) => update(["accueil", "hero", "badge_subtitle"], v)} /></Field>
                            </div>
                        </Section>

                        <Section title="Bloc Intro / Signature">
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.accueil?.intro.overline} onChange={(v) => update(["accueil", "intro", "overline"], v)} /></Field>
                                <Field label="Titre - 1ère partie"><TextInput value={draft.accueil?.intro.title_part1} onChange={(v) => update(["accueil", "intro", "title_part1"], v)} /></Field>
                                <Field label="Titre - 2e partie"><TextInput value={draft.accueil?.intro.title_part2} onChange={(v) => update(["accueil", "intro", "title_part2"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.accueil?.intro.italic} onChange={(v) => update(["accueil", "intro", "italic"], v)} /></Field>
                                <Field label="Titre - fin (ponctuation)"><TextInput value={draft.accueil?.intro.title_part3} onChange={(v) => update(["accueil", "intro", "title_part3"], v)} /></Field>
                                <Field label="Texte" full><TextArea rows={3} value={draft.accueil?.intro.text} onChange={(v) => update(["accueil", "intro", "text"], v)} /></Field>
                            </div>
                        </Section>

                        <Section title="3 services en aperçu">
                            <div className="space-y-6">
                                {draft.accueil?.services_preview?.map((s, i) => (
                                    <div key={i} className="border border-kayama-line p-5 bg-kayama-cream/40">
                                        <p className="overline mb-3">Service 0{i + 1}</p>
                                        <div className="grid md:grid-cols-2 gap-5">
                                            <Field label="Titre"><TextInput value={s.title} onChange={(v) => update(["accueil", "services_preview", i, "title"], v)} /></Field>
                                            <Field label="Icône (users / chef / sparkles)"><TextInput value={s.icon} onChange={(v) => update(["accueil", "services_preview", i, "icon"], v)} /></Field>
                                            <Field label="Description" full><TextArea rows={2} value={s.text} onChange={(v) => update(["accueil", "services_preview", i, "text"], v)} /></Field>
                                            <Field label="Image (URL)" full><ImageInput value={s.image} onChange={(v) => update(["accueil", "services_preview", i, "image"], v)} /></Field>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section title="Bloc Philosophie / Art de recevoir">
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.accueil?.philosophy.overline} onChange={(v) => update(["accueil", "philosophy", "overline"], v)} /></Field>
                                <Field label="Titre - début"><TextInput value={draft.accueil?.philosophy.title_part1} onChange={(v) => update(["accueil", "philosophy", "title_part1"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.accueil?.philosophy.italic} onChange={(v) => update(["accueil", "philosophy", "italic"], v)} /></Field>
                                <Field label="Texte" full><TextArea rows={3} value={draft.accueil?.philosophy.text} onChange={(v) => update(["accueil", "philosophy", "text"], v)} /></Field>
                                <Field label="Image" full><ImageInput value={draft.accueil?.philosophy.image} onChange={(v) => update(["accueil", "philosophy", "image"], v)} /></Field>
                            </div>
                            <div className="mt-6">
                                <p className="overline mb-3">Statistiques (laissez vide pour masquer la section)</p>
                                <ArrayEditor
                                    value={draft.accueil?.philosophy?.stats || []}
                                    onChange={(arr) => update(["accueil", "philosophy", "stats"], arr)}
                                    render={(st, set) => (
                                        <div className="grid md:grid-cols-2 gap-3 w-full">
                                            <Field label="Chiffre"><TextInput value={st.n} onChange={(v) => set({ ...st, n: v })} placeholder="150+" /></Field>
                                            <Field label="Légende"><TextInput value={st.l} onChange={(v) => set({ ...st, l: v })} placeholder="Événements" /></Field>
                                        </div>
                                    )}
                                    newItem={() => ({ n: "100+", l: "Événements" })}
                                />
                            </div>
                        </Section>

                        <Section title="Bannière CTA (bas de l'accueil)">
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Phrase script"><TextInput value={draft.accueil?.cta_banner.script} onChange={(v) => update(["accueil", "cta_banner", "script"], v)} /></Field>
                                <Field label="Titre"><TextInput value={draft.accueil?.cta_banner.title} onChange={(v) => update(["accueil", "cta_banner", "title"], v)} /></Field>
                                <Field label="Texte du bouton"><TextInput value={draft.accueil?.cta_banner.cta} onChange={(v) => update(["accueil", "cta_banner", "cta"], v)} /></Field>
                                <Field label="Image de fond" full><ImageInput value={draft.accueil?.cta_banner.image} onChange={(v) => update(["accueil", "cta_banner", "image"], v)} /></Field>
                            </div>
                        </Section>
                    </TabsContent>

                    {/* APROPOS */}
                    <TabsContent value="apropos" className="space-y-4 mt-8">
                        <SectionHeader title="Page À propos" onReset={() => resetToDefaults("apropos")} />
                        <Section title="Hero" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.apropos?.hero.overline} onChange={(v) => update(["apropos", "hero", "overline"], v)} /></Field>
                                <Field label="Titre - 1ère partie"><TextInput value={draft.apropos?.hero.h1_part1} onChange={(v) => update(["apropos", "hero", "h1_part1"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.apropos?.hero.h1_italic} onChange={(v) => update(["apropos", "hero", "h1_italic"], v)} /></Field>
                                <Field label="Titre - 2e ligne"><TextInput value={draft.apropos?.hero.h1_part2} onChange={(v) => update(["apropos", "hero", "h1_part2"], v)} /></Field>
                            </div>
                        </Section>

                        <Section title="Image & paragraphes d'histoire">
                            <div className="space-y-6">
                                <Field label="Image principale" full><ImageInput value={draft.apropos?.image} onChange={(v) => update(["apropos", "image"], v)} /></Field>
                                <Field label="Phrase script sous l'image"><TextInput value={draft.apropos?.script_under_image} onChange={(v) => update(["apropos", "script_under_image"], v)} /></Field>
                                {draft.apropos?.paragraphs?.map((p, i) => (
                                    <Field key={i} label={`Paragraphe ${i + 1}`} full>
                                        <TextArea rows={4} value={p} onChange={(v) => update(["apropos", "paragraphs", i], v)} />
                                    </Field>
                                ))}
                                <div className="grid md:grid-cols-2 gap-6">
                                    <Field label="« Promesse » - étiquette"><TextInput value={draft.apropos?.promise_label} onChange={(v) => update(["apropos", "promise_label"], v)} /></Field>
                                    <Field label="« Promesse » - valeur"><TextInput value={draft.apropos?.promise_value} onChange={(v) => update(["apropos", "promise_value"], v)} /></Field>
                                    <Field label="« Signature » - étiquette"><TextInput value={draft.apropos?.signature_label} onChange={(v) => update(["apropos", "signature_label"], v)} /></Field>
                                    <Field label="« Signature » - valeur"><TextInput value={draft.apropos?.signature_value} onChange={(v) => update(["apropos", "signature_value"], v)} /></Field>
                                </div>
                            </div>
                        </Section>

                        <Section title="Bannière en bas">
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Image" full><ImageInput value={draft.apropos?.banner?.image} onChange={(v) => update(["apropos", "banner", "image"], v)} /></Field>
                                <Field label="Phrase script"><TextInput value={draft.apropos?.banner?.script} onChange={(v) => update(["apropos", "banner", "script"], v)} /></Field>
                                <Field label="Texte"><TextInput value={draft.apropos?.banner?.text} onChange={(v) => update(["apropos", "banner", "text"], v)} /></Field>
                            </div>
                        </Section>
                    </TabsContent>

                    {/* SERVICES */}
                    <TabsContent value="services" className="space-y-4 mt-8">
                        <SectionHeader title="Page Services" onReset={() => resetToDefaults("services")} />
                        <Section title="Hero" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.services?.hero.overline} onChange={(v) => update(["services", "hero", "overline"], v)} /></Field>
                                <Field label="Titre - début"><TextInput value={draft.services?.hero.h1_part1} onChange={(v) => update(["services", "hero", "h1_part1"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.services?.hero.h1_italic} onChange={(v) => update(["services", "hero", "h1_italic"], v)} /></Field>
                                <Field label="Titre - ponctuation"><TextInput value={draft.services?.hero.h1_part2} onChange={(v) => update(["services", "hero", "h1_part2"], v)} /></Field>
                                <Field label="Sous-titre" full><TextArea rows={2} value={draft.services?.hero.subtitle} onChange={(v) => update(["services", "hero", "subtitle"], v)} /></Field>
                            </div>
                        </Section>

                        {draft.services?.items?.map((it, i) => (
                            <Section key={i} title={`Service ${i + 1} — ${it.title}`}>
                                <div className="grid md:grid-cols-2 gap-5">
                                    <Field label="Titre"><TextInput value={it.title} onChange={(v) => update(["services", "items", i, "title"], v)} /></Field>
                                    <Field label="Icône (heart / briefcase / cake)"><TextInput value={it.icon} onChange={(v) => update(["services", "items", i, "icon"], v)} /></Field>
                                    <Field label="Sous-titre script"><TextInput value={it.sub} onChange={(v) => update(["services", "items", i, "sub"], v)} /></Field>
                                    <Field label="Image" full><ImageInput value={it.image} onChange={(v) => update(["services", "items", i, "image"], v)} /></Field>
                                    <Field label="Description" full><TextArea rows={3} value={it.text} onChange={(v) => update(["services", "items", i, "text"], v)} /></Field>
                                </div>
                                <div className="mt-5">
                                    <p className="overline mb-2">Liste à puces</p>
                                    <ArrayEditor
                                        value={it.bullets || []}
                                        onChange={(arr) => update(["services", "items", i, "bullets"], arr)}
                                        render={(v, set) => <TextInput value={v} onChange={set} />}
                                        newItem={() => "Nouveau point"}
                                    />
                                </div>
                            </Section>
                        ))}

                        <Section title="Bloc « Prestations sur mesure »">
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label="Overline"><TextInput value={draft.services?.extras_overline} onChange={(v) => update(["services", "extras_overline"], v)} /></Field>
                                <Field label="Titre"><TextInput value={draft.services?.extras_title} onChange={(v) => update(["services", "extras_title"], v)} /></Field>
                            </div>
                            <div className="mt-6 space-y-4">
                                {draft.services?.extras?.map((e, i) => (
                                    <div key={i} className="border border-kayama-line p-4 grid md:grid-cols-3 gap-4 bg-kayama-cream/40">
                                        <Field label="Icône (wine / sparkles / truck)"><TextInput value={e.icon} onChange={(v) => update(["services", "extras", i, "icon"], v)} /></Field>
                                        <Field label="Titre"><TextInput value={e.t} onChange={(v) => update(["services", "extras", i, "t"], v)} /></Field>
                                        <Field label="Description"><TextInput value={e.d} onChange={(v) => update(["services", "extras", i, "d"], v)} /></Field>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </TabsContent>

                    {/* GALERIE */}
                    <TabsContent value="galerie" className="space-y-4 mt-8">
                        <SectionHeader title="Galerie photos" onReset={() => resetToDefaults("galerie")} />
                        <Section title="Hero" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.galerie?.hero.overline} onChange={(v) => update(["galerie", "hero", "overline"], v)} /></Field>
                                <Field label="Titre - début"><TextInput value={draft.galerie?.hero.h1_part1} onChange={(v) => update(["galerie", "hero", "h1_part1"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.galerie?.hero.h1_italic} onChange={(v) => update(["galerie", "hero", "h1_italic"], v)} /></Field>
                                <Field label="Titre - ponctuation"><TextInput value={draft.galerie?.hero.h1_part2} onChange={(v) => update(["galerie", "hero", "h1_part2"], v)} /></Field>
                                <Field label="Sous-titre" full><TextArea rows={2} value={draft.galerie?.hero.subtitle} onChange={(v) => update(["galerie", "hero", "subtitle"], v)} /></Field>
                            </div>
                        </Section>

                        <Section title="Photos de la galerie" defaultOpen>
                            <p className="text-xs text-kayama-brown-3 mb-4">
                                Chaque photo s'affiche dans la grille. « Format » détermine la hauteur : <b>tall</b> = grand, <b>short</b> = standard.
                            </p>
                            <ArrayEditor
                                value={draft.galerie?.images || []}
                                onChange={(arr) => update(["galerie", "images"], arr)}
                                render={(img, set) => (
                                    <div className="grid md:grid-cols-2 gap-4 w-full">
                                        <Field label="URL de l'image" full><ImageInput value={img.url} onChange={(v) => set({ ...img, url: v })} /></Field>
                                        <Field label="Légende (alt)"><TextInput value={img.alt} onChange={(v) => set({ ...img, alt: v })} /></Field>
                                        <Field label="Format (tall / short)"><TextInput value={img.h} onChange={(v) => set({ ...img, h: v })} /></Field>
                                    </div>
                                )}
                                newItem={() => ({ url: "", alt: "Nouvelle photo", h: "short" })}
                                stacked
                            />
                        </Section>
                    </TabsContent>

                    {/* MENUS */}
                    <TabsContent value="menus" className="space-y-4 mt-8">
                        <SectionHeader title="Page Menus" onReset={() => resetToDefaults("menus")} />
                        <Section title="Hero" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.menus?.hero.overline} onChange={(v) => update(["menus", "hero", "overline"], v)} /></Field>
                                <Field label="Titre - début"><TextInput value={draft.menus?.hero.h1_part1} onChange={(v) => update(["menus", "hero", "h1_part1"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.menus?.hero.h1_italic} onChange={(v) => update(["menus", "hero", "h1_italic"], v)} /></Field>
                                <Field label="Titre - ponctuation"><TextInput value={draft.menus?.hero.h1_part2} onChange={(v) => update(["menus", "hero", "h1_part2"], v)} /></Field>
                                <Field label="Sous-titre" full><TextArea rows={2} value={draft.menus?.hero.subtitle} onChange={(v) => update(["menus", "hero", "subtitle"], v)} /></Field>
                            </div>
                        </Section>

                        {draft.menus?.formulas?.map((f, i) => (
                            <Section key={i} title={`Formule ${i + 1} — ${f.name}`}>
                                <div className="grid md:grid-cols-3 gap-5">
                                    <Field label="Nom de la formule"><TextInput value={f.name} onChange={(v) => update(["menus", "formulas", i, "name"], v)} /></Field>
                                    <Field label="Baseline script"><TextInput value={f.baseline} onChange={(v) => update(["menus", "formulas", i, "baseline"], v)} /></Field>
                                    <Field label="Prix"><TextInput value={f.price} onChange={(v) => update(["menus", "formulas", i, "price"], v)} /></Field>
                                </div>
                                <div className="mt-5 space-y-4">
                                    {f.sections?.map((sec, si) => (
                                        <div key={si} className="border border-kayama-line p-4 bg-kayama-cream/40">
                                            <div className="flex items-center justify-between mb-3">
                                                <Field label="Titre de section">
                                                    <TextInput value={sec.title} onChange={(v) => update(["menus", "formulas", i, "sections", si, "title"], v)} />
                                                </Field>
                                                <SubBtn
                                                    danger
                                                    onClick={() => {
                                                        const next = f.sections.filter((_, x) => x !== si);
                                                        update(["menus", "formulas", i, "sections"], next);
                                                    }}
                                                >
                                                    <Trash2 size={12} /> Section
                                                </SubBtn>
                                            </div>
                                            <ArrayEditor
                                                value={sec.items || []}
                                                onChange={(arr) => update(["menus", "formulas", i, "sections", si, "items"], arr)}
                                                render={(it, set) => (
                                                    <div className="grid md:grid-cols-2 gap-3 w-full">
                                                        <Field label="Nom du plat"><TextInput value={it.name} onChange={(v) => set({ ...it, name: v })} /></Field>
                                                        <Field label="Description"><TextInput value={it.desc} onChange={(v) => set({ ...it, desc: v })} /></Field>
                                                    </div>
                                                )}
                                                newItem={() => ({ name: "Nouveau plat", desc: "Description" })}
                                                stacked
                                            />
                                        </div>
                                    ))}
                                    <SubBtn
                                        onClick={() => {
                                            const next = [...(f.sections || []), { title: "Nouvelle section", items: [] }];
                                            update(["menus", "formulas", i, "sections"], next);
                                        }}
                                    >
                                        <Plus size={12} /> Ajouter une section
                                    </SubBtn>
                                </div>
                            </Section>
                        ))}

                        <Section title="Bas de page">
                            <div className="grid md:grid-cols-2 gap-5">
                                <Field label="Phrase script"><TextInput value={draft.menus?.bottom_script} onChange={(v) => update(["menus", "bottom_script"], v)} /></Field>
                                <Field label="Texte du bouton"><TextInput value={draft.menus?.bottom_cta} onChange={(v) => update(["menus", "bottom_cta"], v)} /></Field>
                            </div>
                        </Section>
                    </TabsContent>

                    {/* DAILY MENUS */}
                    <TabsContent value="daily_menus" className="space-y-4 mt-8">
                        <SectionHeader title="Menus du jour" onReset={() => resetToDefaults("daily_menus")} />
                        <Section title="En-tête de la section" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Activer la section sur le site">
                                    <select
                                        className="input-k"
                                        value={draft.daily_menus?.enabled ? "1" : "0"}
                                        onChange={(e) => update(["daily_menus", "enabled"], e.target.value === "1")}
                                        data-testid="daily-menus-enabled"
                                    >
                                        <option value="1">Oui (afficher)</option>
                                        <option value="0">Non (masquer)</option>
                                    </select>
                                </Field>
                                <Field label="Overline"><TextInput value={draft.daily_menus?.overline} onChange={(v) => update(["daily_menus", "overline"], v)} /></Field>
                                <Field label="Titre - début"><TextInput value={draft.daily_menus?.title_part1} onChange={(v) => update(["daily_menus", "title_part1"], v)} /></Field>
                                <Field label="Titre - italique"><TextInput value={draft.daily_menus?.italic} onChange={(v) => update(["daily_menus", "italic"], v)} /></Field>
                                <Field label="Titre - fin"><TextInput value={draft.daily_menus?.title_part2} onChange={(v) => update(["daily_menus", "title_part2"], v)} /></Field>
                                <Field label="Sous-titre" full><TextArea rows={2} value={draft.daily_menus?.subtitle} onChange={(v) => update(["daily_menus", "subtitle"], v)} /></Field>
                            </div>
                        </Section>

                        <Section title="Liste des plats du jour" defaultOpen>
                            <p className="text-xs text-kayama-brown-3 mb-4">
                                Ajoutez chaque plat avec sa photo, son prix et sa description. Vous pouvez réorganiser et mettre à jour la liste chaque jour.
                            </p>
                            <ArrayEditor
                                value={draft.daily_menus?.items || []}
                                onChange={(arr) => update(["daily_menus", "items"], arr)}
                                render={(it, set) => (
                                    <div className="grid md:grid-cols-2 gap-4 w-full">
                                        <Field label="Jour / date">
                                            <TextInput value={it.date} onChange={(v) => set({ ...it, date: v })} placeholder="Aujourd'hui, Vendredi, 13 mai…" />
                                        </Field>
                                        <Field label="Prix"><TextInput value={it.price} onChange={(v) => set({ ...it, price: v })} placeholder="12 €" /></Field>
                                        <Field label="Nom du plat" full><TextInput value={it.name} onChange={(v) => set({ ...it, name: v })} /></Field>
                                        <Field label="Description" full><TextArea rows={2} value={it.desc} onChange={(v) => set({ ...it, desc: v })} /></Field>
                                        <Field label="Photo (uploader ou coller URL)" full><ImageInput value={it.image} onChange={(v) => set({ ...it, image: v })} /></Field>
                                    </div>
                                )}
                                newItem={() => ({ date: "Aujourd'hui", name: "Nouveau plat", desc: "", price: "", image: "" })}
                                stacked
                            />
                        </Section>
                    </TabsContent>

                    {/* CONTACT PAGE */}
                    <TabsContent value="contact_page" className="space-y-4 mt-8">
                        <SectionHeader title="Page Contact" onReset={() => resetToDefaults("contact_page")} />
                        <Section title="Hero" defaultOpen>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.contact_page?.hero.overline} onChange={(v) => update(["contact_page", "hero", "overline"], v)} /></Field>
                                <Field label="Titre - début"><TextInput value={draft.contact_page?.hero.h1_part1} onChange={(v) => update(["contact_page", "hero", "h1_part1"], v)} /></Field>
                                <Field label="Titre - italique rose"><TextInput value={draft.contact_page?.hero.h1_italic} onChange={(v) => update(["contact_page", "hero", "h1_italic"], v)} /></Field>
                                <Field label="Titre - ponctuation"><TextInput value={draft.contact_page?.hero.h1_part2} onChange={(v) => update(["contact_page", "hero", "h1_part2"], v)} /></Field>
                                <Field label="Sous-titre" full><TextArea rows={2} value={draft.contact_page?.hero.subtitle} onChange={(v) => update(["contact_page", "hero", "subtitle"], v)} /></Field>
                            </div>
                        </Section>
                        <Section title="Bloc coordonnées">
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline"><TextInput value={draft.contact_page?.coords_overline} onChange={(v) => update(["contact_page", "coords_overline"], v)} /></Field>
                                <Field label="Titre"><TextInput value={draft.contact_page?.coords_title} onChange={(v) => update(["contact_page", "coords_title"], v)} /></Field>
                                <Field label="Phrase script"><TextInput value={draft.contact_page?.coords_script} onChange={(v) => update(["contact_page", "coords_script"], v)} /></Field>
                            </div>
                        </Section>
                        <Section title="Formulaire & message de succès">
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Overline du formulaire"><TextInput value={draft.contact_page?.form_overline} onChange={(v) => update(["contact_page", "form_overline"], v)} /></Field>
                                <Field label="Titre du formulaire"><TextInput value={draft.contact_page?.form_title} onChange={(v) => update(["contact_page", "form_title"], v)} /></Field>
                                <Field label="Succès - titre"><TextInput value={draft.contact_page?.success_title} onChange={(v) => update(["contact_page", "success_title"], v)} /></Field>
                                <Field label="Succès - phrase script"><TextInput value={draft.contact_page?.success_script} onChange={(v) => update(["contact_page", "success_script"], v)} /></Field>
                                <Field label="Succès - texte" full><TextArea rows={3} value={draft.contact_page?.success_text} onChange={(v) => update(["contact_page", "success_text"], v)} /></Field>
                            </div>
                        </Section>
                    </TabsContent>
                </Tabs>

                <div className="mt-12 flex justify-end">
                    <button onClick={save} disabled={!dirty || saving} className="btn-primary-k disabled:opacity-50" data-testid="content-save-bottom">
                        <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer toutes les modifications"}
                    </button>
                </div>
            </div>

            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="max-w-2xl rounded-none border-kayama-line" data-testid="history-dialog">
                    <DialogHeader>
                        <p className="overline">Historique des sauvegardes</p>
                        <DialogTitle className="font-heading text-3xl font-light text-kayama-brown">
                            Restaurer une version
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-kayama-brown-2 -mt-2">
                        Une copie est automatiquement créée à chaque enregistrement. Vous pouvez revenir à n'importe quelle version précédente.
                    </p>
                    <div className="mt-4 max-h-[60vh] overflow-y-auto">
                        {loadingHistory ? (
                            <p className="text-center py-8 text-kayama-brown-3">Chargement…</p>
                        ) : history.length === 0 ? (
                            <p className="text-center py-8 text-kayama-brown-3">Aucune sauvegarde antérieure.</p>
                        ) : (
                            <ul className="divide-y divide-kayama-line">
                                {history.map((h) => (
                                    <li key={h.snapshot_at} className="py-4 flex items-center justify-between gap-3" data-testid={`history-${h.snapshot_at}`}>
                                        <div>
                                            <p className="font-heading text-lg text-kayama-brown">
                                                {new Date(h.snapshot_at).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                            <p className="text-xs text-kayama-brown-3">
                                                {h.saved_by || "—"} {h.note ? `· ${h.note}` : ""}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => restoreSnapshot(h.snapshot_at)}
                                            className="btn-outline-k"
                                            data-testid={`restore-${h.snapshot_at}`}
                                        >
                                            <RotateCcw size={12} /> Restaurer
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SectionHeader({ title, onReset }) {
    return (
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <h2 className="font-heading text-3xl text-kayama-brown">{title}</h2>
            <button type="button" onClick={onReset} className="text-xs text-kayama-brown-3 hover:text-kayama-pink flex items-center gap-1 uppercase tracking-[0.15em]">
                <RotateCcw size={12} /> Réinitialiser cette section
            </button>
        </div>
    );
}

function ArrayEditor({ value, onChange, render, newItem, stacked }) {
    return (
        <div className="space-y-3">
            {value.map((v, i) => (
                <div key={i} className={`flex ${stacked ? "flex-col md:flex-row" : "flex-row"} items-start gap-3 border border-kayama-line p-3 bg-kayama-paper`}>
                    <div className="flex-1 w-full">
                        {render(v, (nv) => {
                            const next = [...value];
                            next[i] = nv;
                            onChange(next);
                        })}
                    </div>
                    <SubBtn
                        danger
                        onClick={() => onChange(value.filter((_, x) => x !== i))}
                    >
                        <Trash2 size={12} />
                    </SubBtn>
                </div>
            ))}
            <SubBtn onClick={() => onChange([...value, newItem()])}>
                <Plus size={12} /> Ajouter
            </SubBtn>
        </div>
    );
}
